import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Globe,
  ExternalLink,
  Search,
  Copy,
  GraduationCap,
  Microscope,
  BookMarked,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELIABILITY_BADGE = {
  'peer-reviewed': { label: 'Peer-reviewed', color: 'bg-green-100 text-green-800 border-green-300' },
  'lab-page': { label: 'Lab page', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  'trade-publication': { label: 'Trade pub.', color: 'bg-amber-100 text-amber-800 border-amber-300' },
};

export default function ResearchLibrary() {
  const [institutions, setInstitutions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [focusFilter, setFocusFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [inst, t] = await Promise.all([
          axios.get(`${API}/research-library/institutions`),
          axios.get(`${API}/research-library/tags`),
        ]);
        setInstitutions(inst.data.institutions || []);
        setTags(t.data.tags || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const countries = useMemo(() => {
    const set = new Set(institutions.map((i) => i.country));
    return Array.from(set).sort();
  }, [institutions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return institutions.filter((i) => {
      if (countryFilter && i.country !== countryFilter) return false;
      if (focusFilter && !(i.focus_areas || []).includes(focusFilter)) return false;
      if (!q) return true;
      const haystack = [
        i.name,
        i.short_name,
        i.lab_name,
        i.lead_researcher,
        i.country,
        i.city,
        i.description,
        (i.focus_areas || []).join(' '),
        (i.citations || []).map((c) => `${c.title} ${c.authors}`).join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [institutions, query, countryFilter, focusFilter]);

  const totalCitations = useMemo(
    () => institutions.reduce((sum, i) => sum + (i.citations?.length || 0), 0),
    [institutions]
  );

  const copyApa = (apa) => {
    navigator.clipboard.writeText(apa);
    toast.success('APA citation copied');
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="research-library-page">
      <SEO
        title="Research Library — Canine Science Bibliography"
        description="Curated directory of universities and research institutions studying dog cognition, behavior, genetics, and health. Peer-reviewed citations with direct links to source material."
        keywords={[
          'dog research',
          'canine cognition research',
          'dog science papers',
          'dog behavior research',
          'canine genetics',
          'university dog research',
          'peer-reviewed dog studies',
          'dog cognition bibliography',
        ]}
        url="/research-library"
      />

      <header className="mb-10 border-b pb-8">
        <div className="flex items-center gap-3 mb-3">
          <BookMarked className="w-9 h-9 text-amber-600" />
          <h1 className="text-4xl sm:text-5xl font-heading font-semibold">Research Library</h1>
        </div>
        <p className="text-base text-muted-foreground max-w-3xl leading-relaxed">
          A curated bibliography of the universities and institutions advancing our understanding
          of dogs. Every entry links directly to peer-reviewed publications or accredited lab
          pages — no blogs, no hearsay. Use this as your reference when sharing, citing, or
          simply learning more about the science behind our companions.
        </p>
        <div className="flex flex-wrap gap-4 mt-5 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <GraduationCap className="w-4 h-4" />
            {institutions.length} institutions
          </span>
          <span className="flex items-center gap-1">
            <Microscope className="w-4 h-4" />
            {totalCitations} citations
          </span>
          <span className="flex items-center gap-1">
            <Globe className="w-4 h-4" />
            {countries.length} countries
          </span>
        </div>
      </header>

      {/* Filters */}
      <div className="mb-8 grid gap-4 md:grid-cols-[1fr,auto,auto]">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder="Search institution, researcher, citation…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="research-search-input"
          />
        </div>
        <select
          className="border rounded-md px-3 py-2 bg-background"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          data-testid="country-filter"
        >
          <option value="">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="border rounded-md px-3 py-2 bg-background"
          value={focusFilter}
          onChange={(e) => setFocusFilter(e.target.value)}
          data-testid="focus-filter"
        >
          <option value="">All focus areas</option>
          {tags.map((t) => (
            <option key={t.tag} value={t.tag}>
              {t.tag} ({t.count})
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-center py-12 text-muted-foreground">Loading research directory…</p>
      ) : filtered.length === 0 ? (
        <p className="text-center py-12 text-muted-foreground">
          No institutions match those filters.
        </p>
      ) : (
        <div className="space-y-8">
          {filtered.map((inst) => {
            const rel = RELIABILITY_BADGE[inst.reliability] || RELIABILITY_BADGE['peer-reviewed'];
            return (
              <Card
                key={inst.id}
                className="overflow-hidden border-l-4 border-l-amber-500"
                data-testid={`institution-${inst.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h2 className="text-xl font-heading font-semibold leading-snug">
                        {inst.name}
                      </h2>
                      {inst.lab_name && inst.lab_name !== inst.name && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {inst.lab_name}
                        </p>
                      )}
                      <p className="text-sm mt-1">
                        <span className="text-muted-foreground">
                          {inst.city ? `${inst.city}, ` : ''}{inst.country}
                        </span>
                        {inst.lead_researcher && (
                          <span className="ml-2">
                            · Led by <strong>{inst.lead_researcher}</strong>
                          </span>
                        )}
                      </p>
                    </div>
                    <Badge variant="outline" className={rel.color}>
                      {rel.label}
                    </Badge>
                  </div>

                  {inst.description && (
                    <p className="text-sm leading-relaxed mb-4">{inst.description}</p>
                  )}

                  {inst.focus_areas?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {inst.focus_areas.map((f) => (
                        <Badge key={f} variant="secondary" className="text-xs">
                          {f}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="mb-4"
                  >
                    <a
                      href={inst.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`visit-${inst.id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Visit lab
                    </a>
                  </Button>

                  {inst.citations?.length > 0 && (
                    <details className="border-t pt-4 mt-2">
                      <summary className="cursor-pointer font-medium text-sm flex items-center gap-1.5 hover:text-amber-700">
                        <BookOpen className="w-4 h-4" />
                        {inst.citations.length} citation{inst.citations.length !== 1 ? 's' : ''}
                      </summary>
                      <ul className="mt-3 space-y-4 pl-2">
                        {inst.citations.map((c) => (
                          <li key={c.id} className="text-sm border-l-2 border-muted pl-3">
                            <div className="font-medium leading-snug">
                              <a
                                href={c.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-amber-700 hover:underline"
                              >
                                {c.title}
                              </a>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {c.authors} · {c.journal || '—'}
                              {c.year ? ` · ${c.year}` : ''}
                            </div>
                            {c.doi && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                DOI:{' '}
                                <a
                                  href={`https://doi.org/${c.doi}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline"
                                >
                                  {c.doi}
                                </a>
                              </div>
                            )}
                            {c.apa && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-1 h-7 text-xs"
                                onClick={() => copyApa(c.apa)}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy APA
                              </Button>
                            )}
                            {c.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {c.tags.map((t) => (
                                  <Badge key={t} variant="outline" className="text-[10px] py-0">
                                    {t}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-12 pt-8 border-t text-center text-sm text-muted-foreground">
        <p>
          Know an institution doing solid canine research we've missed?{' '}
          <Link to="/content-suggestions" className="underline text-amber-700">
            Suggest it here
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
